import { ChevronDownIcon, GlobusIcon } from '@fingertip/icons'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { format, fromZonedTime, toZonedTime } from 'date-fns-tz'
import { observer } from 'mobx-react-lite'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import {
  formatTimezoneLabel,
  TIME_ZONE_GROUPS,
  TIME_ZONE_MAPPINGS,
} from '@/lib/dates/time-zone'
import { RootStoreContext } from '@/lib/stores/root-store'
import { cn } from '@/lib/utils'

import { Dialog, DialogContent, DialogTitle } from '../ui/dialog'
import { Input } from '../ui/input'
import { Label } from '../ui/label'

export interface TimeZoneOption {
  id: string
  label: string
  description: string
  group: string
  selected?: boolean
}

export const TimeZonePicker = observer(() => {
  const { t } = useTranslation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [currentTime, setCurrentTime] = useState(() => new Date())
  const [search, setSearch] = useState('')
  const {
    bookerStore: { timeZone, setTimeZone },
  } = useContext(RootStoreContext)
  const containerRef = useRef<HTMLDivElement>(null)

  const scrollToSelected = useCallback(() => {
    setTimeout(() => {
      if (!containerRef.current) return

      const selectedElement = containerRef.current.querySelector(
        '[data-selected="true"]',
      )

      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'auto',
          block: 'center',
        })
      }
    }, 0)
  }, [])

  useEffect(() => {
    if (dialogOpen) {
      scrollToSelected()
    }
  }, [dialogOpen, scrollToSelected])

  const timeZoneOptions = useMemo(() => {
    return Object.entries(TIME_ZONE_MAPPINGS).map(
      ([label, { zone, group }]) => {
        const zonedTime = toZonedTime(currentTime, zone)

        const currentTimeString = format(zonedTime, 'h:mm aaa', {
          timeZone: zone,
        })

        return {
          id: zone,
          label,
          description: `${currentTimeString}`,
          group: TIME_ZONE_GROUPS[group],
          selected: zone === timeZone,
        }
      },
    )
  }, [currentTime, timeZone])

  const groupedOptions = useMemo(() => {
    const filtered = timeZoneOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(search.toLowerCase()) ||
        option.description.toLowerCase().includes(search.toLowerCase()) ||
        option.group.toLowerCase().includes(search.toLowerCase()),
    )

    return filtered.reduce(
      (acc, option) => {
        const group = option.group
        if (!acc[group]) acc[group] = []
        acc[group].push(option)
        return acc
      },
      {} as Record<string, TimeZoneOption[]>,
    )
  }, [timeZoneOptions, search])

  useEffect(() => {
    let frameId: number
    let lastUpdate = Date.now()

    const updateTime = () => {
      const now = Date.now()
      if (now - lastUpdate >= 60000) {
        setCurrentTime(new Date())
        lastUpdate = now
      }
      frameId = requestAnimationFrame(updateTime)
    }

    frameId = requestAnimationFrame(updateTime)
    return () => cancelAnimationFrame(frameId)
  }, [])

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <button
        className="group w-full text-left"
        type="button"
        onClick={() => setDialogOpen(true)}
      >
        <Label>{t('time_zone')}</Label>
        <div className="flex w-full items-center text-left">
          <GlobusIcon className="mr-1 size-4" />
          <div className="truncate group-hover:underline">
            {formatTimezoneLabel(timeZone)}
          </div>
          <div className="text-sm opacity-70">
            &nbsp;(
            {format(fromZonedTime(currentTime, timeZone), 'h:mm aaa', {
              timeZone,
            })}
            )
          </div>
          <ChevronDownIcon className="ml-1 size-4" />
        </div>
      </button>

      <DialogContent className="max-h-[calc(100vh-100px)] min-h-[calc(100vh-100px)] overflow-y-auto p-0">
        <VisuallyHidden>
          <DialogTitle>{t('time_zone')}</DialogTitle>
        </VisuallyHidden>

        <div className="space-y-4" ref={containerRef}>
          <div className="sticky top-0 z-10 bg-linear-to-b from-background via-background to-transparent  px-4 pt-4">
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('search_timezones')}
              clearable
              onClear={() => setSearch('')}
            />
          </div>

          <div className="space-y-6 pb-4">
            {Object.entries(groupedOptions).map(([group, options]) => (
              <div key={group} className="space-y-1">
                <h3 className="h4 px-4">{group}</h3>
                {options.map((option) => (
                  <button
                    key={option.id}
                    data-selected={option.selected}
                    className={cn(
                      'flex w-full items-center justify-between py-2 px-4 text-sm hover:bg-accent',
                      {
                        'bg-primary! text-primary-foreground!': option.selected,
                      },
                    )}
                    onClick={() => {
                      setTimeZone(option.id)
                      setDialogOpen(false)
                    }}
                  >
                    <span>{option.label}</span>
                    <span className="opacity-70">{option.description}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
})
